import React from 'react';
import { useParams } from 'react-router-dom';
import NavigationBar from '../components/Navbar';
import Footer from '../components/Footer';
import EditableText from '../components/EditableText';
import { LanguageContext } from '../contexts/LanguageContext';
import { Helmet } from 'react-helmet';
import axios from 'axios';

type SetupStatus = {
  id: string;
  status: string;
  email?: string;
  companyName?: string;
  created_at?: string;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const BOOKINGS_URL = process.env.REACT_APP_MS_BOOKINGS_URL || '';

const SetupStatusPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = React.useContext(LanguageContext);
  const [data, setData] = React.useState<SetupStatus | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setError(null);
        const res = await axios.get(`${API_BASE}/customer-setup/${id}`);
        if (!mounted) return;
        setData(res.data);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || 'Not found');
      }
    };
    if (id) run();
    return () => { mounted = false };
  }, [id]);

  const isHTTPS = typeof window !== 'undefined' && window.location.protocol === 'https:';

  return (
    <div className='grips-bg d-flex flex-column min-vh-100'>
      <Helmet>
        <title>{language === 'de' ? 'Status Ihrer Konfiguration' : 'Your configuration status'}</title>
      </Helmet>
      <NavigationBar />
      <div className='container flex-grow-1 py-5 text-white'>
        <h1 className='mb-3'>{language === 'de' ? 'Status Ihrer Konfiguration' : 'Configuration Status'}</h1>
        <div className='mb-3'>
          <EditableText id={language === 'de' ? '722' : '723'} />
        </div>
        {error && <div className='alert alert-danger'>{error}</div>}
        {data && (
          <div className='card bg-transparent border-light mb-4'>
            <div className='card-body'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <div className='text-light-50'>{language === 'de' ? 'Ihre ID' : 'Your ID'}</div>
                  <div className='fw-bold'>{data.id}</div>
                </div>
                <div className='col-md-3'>
                  <div className='text-light-50'>{language === 'de' ? 'Status' : 'Status'}</div>
                  <div className='fw-bold'>{data.status}</div>
                </div>
                <div className='col-md-3'>
                  <div className='text-light-50'>{language === 'de' ? 'E-Mail' : 'Email'}</div>
                  <div className='fw-bold'>{data.email}</div>
                </div>
                <div className='col-md-3'>
                  <div className='text-light-50'>{language === 'de' ? 'Firma' : 'Company'}</div>
                  <div className='fw-bold'>{data.companyName}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <h2 className='h4 mb-3'>{language === 'de' ? 'Termin buchen' : 'Book an appointment'}</h2>
        {!BOOKINGS_URL ? (
          <div className='alert alert-warning'>
            {language === 'de' ? 'Bitte REACT_APP_MS_BOOKINGS_URL in der .env konfigurieren, um die Terminbuchung einzubetten.' : 'Please configure REACT_APP_MS_BOOKINGS_URL in .env to embed Bookings.'}
          </div>
        ) : !isHTTPS ? (
          <div className='alert alert-info'>
            {language === 'de'
              ? 'Für die Einbettung erfordert Microsoft HTTPS. Öffnen Sie die Buchungsseite über den folgenden Link oder starten Sie die App lokal mit HTTPS.'
              : 'Microsoft requires HTTPS for embedding. Open the booking page via the link below or run the app locally with HTTPS.'}
            <div className='mt-2'>
              <a className='btn btn-outline-light btn-sm' href={BOOKINGS_URL} target='_blank' rel='noreferrer'>
                {language === 'de' ? 'Buchungsseite öffnen' : 'Open booking page'}
              </a>
            </div>
          </div>
        ) : (
          <div className='ratio ratio-16x9 mb-4'>
            <iframe src={BOOKINGS_URL} title='Microsoft Bookings' style={{ border: 0 }} allowFullScreen></iframe>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SetupStatusPage;
